import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { catchError, interval, of, startWith, switchMap } from 'rxjs';

import { AlertasService, MisAlertasResponse } from './alertas.service';

@Component({
  selector: 'inv-alertas-tareas',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [
    CommonModule,
    DatePipe,
    RouterModule,
    MatBadgeModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
  ],
  styles: [
    `
      :host { display: inline-flex; }

      /* El panel de mat-menu trae max-width: 280px, que recortaba estos 380px
         (por eso el panel se veía cortado). La clase va en el <mat-menu>, que
         Material copia al panel del overlay; como ese panel vive fuera del DOM
         del componente, hace falta ::ng-deep para alcanzarlo. */
      ::ng-deep .alertas-panel-wrapper.mat-mdc-menu-panel {
        max-width: none;
      }

      /* max-width por viewport para que en pantallas chicas no se desborde. */
      .alertas-panel {
        width: 380px;
        max-width: calc(100vw - 2rem);
        max-height: 70vh;
        overflow-y: auto;
      }
      .seccion-titulo { font-size: 11px; text-transform: uppercase; letter-spacing: 0.05em; }
    `,
  ],
  template: `
    <button
      mat-icon-button
      [matMenuTriggerFor]="menu"
      [matTooltip]="
        alertas?.total
          ? 'Tienes ' + alertas?.total + ' tareas para revisar'
          : 'Sin alertas pendientes'
      "
      class="!text-default"
    >
      <mat-icon
        *ngIf="(alertas?.criticas?.length ?? 0) > 0; else iconoSinCriticas"
        [matBadge]="alertas?.total"
        matBadgeColor="warn"
        matBadgeSize="small"
        class="text-red-600"
      >notification_important</mat-icon>
      <ng-template #iconoSinCriticas>
        <mat-icon
          [matBadge]="(alertas?.total ?? 0) > 0 ? alertas?.total : null"
          matBadgeColor="accent"
          matBadgeSize="small"
        >notifications</mat-icon>
      </ng-template>
    </button>

    <mat-menu #menu="matMenu" class="alertas-panel-wrapper" xPosition="before">
      <div class="alertas-panel" (click)="$event.stopPropagation()">
        <div class="px-4 pt-3 pb-2 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div class="flex items-center justify-between">
            <div class="font-semibold text-gray-900 flex items-center gap-2">
              <mat-icon class="!text-base !w-5 !h-5">checklist_rtl</mat-icon>
              Mis alertas
              <!-- De qué empresa es este aviso. Las alertas de tareas son de obra
                   (Invering) y se mezclaban con las de Infinitus sin distinguirse. -->
              <span class="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 border border-slate-300">
                Invering
              </span>
            </div>
            <button
              mat-icon-button
              (click)="cargar(); $event.stopPropagation()"
              matTooltip="Recargar"
              class="!w-8 !h-8"
            >
              <mat-icon class="!text-base !w-5 !h-5">refresh</mat-icon>
            </button>
          </div>
          <div *ngIf="alertas" class="text-xs text-secondary mt-1">
            {{ alertas.total }} para revisar ·
            <span class="text-red-700 font-semibold">{{ alertas.criticas.length }} críticas</span>
            · <span class="text-amber-700">{{ alertas.vencidas.length }} vencidas</span>
            · <span class="text-blue-700">{{ alertas.proximas7Dias.length }} próximas</span>
          </div>

          <!-- Si el sondeo falla, se dice. Sin esto la campana se queda con lo
               último que supo y se ve idéntica a una al día — que es cómo un corte
               de red terminaba pareciendo «no tienes nada pendiente». -->
          <div
            *ngIf="desconectado"
            class="mt-2 text-xs rounded bg-amber-50 border border-amber-200 text-amber-900 px-2 py-1">
            Sin conexión con el servidor: esto puede estar desactualizado.
          </div>
        </div>

        <!-- Críticas -->
        <ng-container *ngIf="(alertas?.criticas?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-red-700 font-bold">
            ⚠ Críticas (penalidad legal)
          </div>
          <a
            *ngFor="let t of alertas?.criticas"
            mat-menu-item
            [routerLink]="['/apps/proyectos/contratos', t.proyectoId, 'editar']"
            class="!h-auto !py-3"
          >
            <div class="flex flex-col gap-0.5 w-full">
              <div class="flex items-start gap-2">
                <mat-icon class="text-red-600 !text-base !w-5 !h-5 flex-shrink-0">warning</mat-icon>
                <span class="text-sm font-medium whitespace-normal leading-tight">
                  {{ t.titulo }}
                </span>
              </div>
              <div class="text-[11px] text-secondary pl-7">
                {{ t.proyectoCodigo }} · {{ t.cargoNombre || t.areaNombre || '—' }}
                <span *ngIf="t.fechaLimite"> · vence {{ t.fechaLimite | date:'shortDate' }}</span>
              </div>
              <div
                *ngIf="t.consecuenciaIncumplimiento"
                class="text-[11px] text-red-700 pl-7 italic whitespace-normal leading-tight mt-0.5"
              >
                {{ t.consecuenciaIncumplimiento }}
              </div>
            </div>
          </a>
        </ng-container>

        <!-- Vencidas -->
        <ng-container *ngIf="(alertas?.vencidas?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-amber-700 font-bold">
            ⏰ Vencidas
          </div>
          <a
            *ngFor="let t of alertas?.vencidas"
            mat-menu-item
            [routerLink]="['/apps/proyectos/contratos', t.proyectoId, 'editar']"
            class="!h-auto !py-2"
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm whitespace-normal leading-tight">{{ t.titulo }}</span>
              <div class="text-[11px] text-secondary">
                {{ t.proyectoCodigo }} · venció {{ t.fechaLimite | date:'shortDate' }}
              </div>
            </div>
          </a>
        </ng-container>

        <!-- Próximas -->
        <ng-container *ngIf="(alertas?.proximas7Dias?.length ?? 0) > 0">
          <div class="px-4 pt-3 pb-1 seccion-titulo text-blue-700 font-bold">
            📅 Próximas 7 días
          </div>
          <a
            *ngFor="let t of alertas?.proximas7Dias"
            mat-menu-item
            [routerLink]="['/apps/proyectos/contratos', t.proyectoId, 'editar']"
            class="!h-auto !py-2"
          >
            <div class="flex flex-col gap-0.5">
              <span class="text-sm whitespace-normal leading-tight">{{ t.titulo }}</span>
              <div class="text-[11px] text-secondary">
                {{ t.proyectoCodigo }} · vence {{ t.fechaLimite | date:'shortDate' }}
              </div>
            </div>
          </a>
        </ng-container>

        <!-- Empty state -->
        <div
          *ngIf="alertas && alertas.total === 0"
          class="text-center py-10 text-secondary"
        >
          <mat-icon class="!text-5xl !w-12 !h-12 text-gray-300 mb-2">check_circle</mat-icon>
          <p class="text-sm">Todo al día — sin alertas pendientes.</p>
        </div>

        <!-- Footer link al dashboard -->
        <div
          *ngIf="alertas && alertas.total > 0"
          class="px-4 py-3 border-t border-gray-200 sticky bottom-0 bg-white"
        >
          <a
            mat-stroked-button
            class="!w-full"
            routerLink="/apps/proyectos/tareas"
          >
            Ver dashboard de tareas
          </a>
        </div>
      </div>
    </mat-menu>
  `,
})
export class AlertasTareasComponent implements OnInit {
  private readonly service = inject(AlertasService);
  private readonly destroyRef = inject(DestroyRef);

  alertas: MisAlertasResponse | null = null;

  /** true si el último sondeo falló. Lo que se ve puede estar viejo. */
  desconectado = false;

  ngOnInit(): void {
    // Polling cada 90 segundos (las críticas no pueden esperar más, y el
    // costo de la query es bajo: hits agregados a inv_tasks).
    interval(90_000)
      .pipe(
        startWith(0),
        // `catchError` va DENTRO del switchMap, no fuera.
        //
        // Si el error del observable interno sube al externo, RxJS termina el
        // stream: el `error` handler de abajo lo silenciaba pero el interval
        // quedaba muerto para siempre. Un corte de red de cinco segundos dejaba
        // la campana sin volver a sondear en toda la sesión — y sin badge, que
        // se lee como «no hay nada pendiente». Devolver `null` acá mantiene vivo
        // el ciclo y marca la desconexión sin borrar lo último que sí se supo.
        switchMap(() => this.service.misAlertas().pipe(catchError(() => of(null)))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (a) => {
          if (a === null) {
            // No se pudo consultar. NO se borra `alertas`: mostrar cero sería
            // indistinguible de «no hay pendientes», que es lo contrario de lo
            // que está pasando. Se conserva lo último conocido y se avisa.
            this.desconectado = true;
            return;
          }
          this.desconectado = false;
          this.alertas = a;
        },
      });
  }

  cargar(): void {
    this.service.misAlertas().subscribe({
      next: (a) => (this.alertas = a),
      error: () => undefined,
    });
  }
}
